import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, TableCell, TableSortLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DynamicTooltip } from 'shared/ui/Tooltip';

import { getHeaderCellSize } from './columnWidth';
import { getDefaultAlign } from './getDefaultAlign';

import type { ReportWidgetColumnConfig } from '../../model/types';

interface ReportWidgetTableHeaderCellProps {
    column: ReportWidgetColumnConfig;
    measuredWidth?: number;
    isPrinting?: boolean;
    sortColumnId?: string;
    sortDirection?: 'asc' | 'desc';
    onSort?: (columnId: string) => void;
}

export const ReportWidgetTableHeaderCell = ({
    column,
    measuredWidth,
    isPrinting = false,
    sortColumnId,
    sortDirection,
    onSort,
}: ReportWidgetTableHeaderCellProps) => {
    const { t } = useTranslation();
    const { width, minWidth, maxWidth } = getHeaderCellSize({
        column,
        measuredWidth,
        isPrinting,
    });
    const canSort = Boolean(column.sortable && onSort && !isPrinting);
    const isActive = canSort && sortColumnId === column.id;
    const sortLabelColor = isActive ? 'common.black' : 'inherit';
    const label = canSort ? (
        <TableSortLabel
            active={isActive}
            direction={isActive ? sortDirection ?? 'asc' : 'asc'}
            hideSortIcon={!isActive}
            onClick={() => onSort?.(column.id)}
            aria-label={t('composableReportWidget.sortByColumn', {
                column: column.label,
            })}
            sx={{
                color: sortLabelColor,
                '&:hover': {
                    color: sortLabelColor,
                },
                '&:focus': {
                    color: sortLabelColor,
                },
                '&.Mui-active': {
                    color: 'common.black',
                },
                '& .MuiTableSortLabel-icon': {
                    fontSize: '1.4rem',
                    color: sortLabelColor,
                    opacity: isActive ? 1 : undefined,
                },
                '&.Mui-active .MuiTableSortLabel-icon': {
                    color: 'common.black',
                    opacity: 1,
                },
                '&:hover .MuiTableSortLabel-icon': {
                    color: sortLabelColor,
                    opacity: isActive ? 1 : undefined,
                },
            }}
        >
            {column.label}
        </TableSortLabel>
    ) : (
        column.label
    );

    return (
        <TableCell
            key={column.id}
            align={getDefaultAlign(column)}
            aria-sort={
                isActive
                    ? sortDirection === 'asc'
                        ? 'ascending'
                        : 'descending'
                    : undefined
            }
            sx={{
                color: 'text.secondary',
                width,
                minWidth,
                maxWidth,
                whiteSpace: isPrinting ? 'normal' : 'nowrap',
            }}
        >
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    columnGap: '4px',
                }}
            >
                {label}
                {column.headerTooltip && (
                    <DynamicTooltip
                        content={column.headerTooltip}
                        containerStyle={{
                            paddingTop: '4px',
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{
                                fontSize: '1.4rem',
                            }}
                            data-pdf-tooltip="true"
                        />
                    </DynamicTooltip>
                )}
            </Box>
        </TableCell>
    );
};
